import { z } from "zod";
import { IsoDateTime, QueryBool, Uuid } from "./common.js";

// ---- The laboratory's letterhead (pilot request 2026-11-02, migration 0023, ADR 0024) ----------
//
// The Administrador uploads the laboratory's letterhead in Configuración → Membrete: a one-page
// PDF (preferred, it stays vector) or a PNG/JPEG of the page. Every new result report is laid out
// inside the letterhead's free zone ("zona libre") and, "con membrete", stamped on top of it. The
// same zone is used "sin membrete", for paper that already carries the letterhead.

/** Caps and defaults shared by the web app, the API and the operator CLI. Points of a US Letter page (612 × 792). */
export const LETTERHEAD_LIMITS = {
  maxBytes: 2 * 1024 * 1024,
  /** Each margin of the free zone, in points (½ in to 4½ in). */
  minMarginPt: 36,
  maxMarginPt: 324,
  /** At least 5 in of the page stays for the report itself. */
  minBodyPt: 360,
  /**
   * Starting free zone for any laboratory, adjusted per laboratory in Configuración. Measured on a
   * typical Letter letterhead (the pilot's, 2026-11-02): header ink ending 151 pt from the top,
   * footer ink starting 92 pt above the bottom edge; 14 and 12 pt of air added.
   */
  defaultTopPt: 165,
  defaultBottomPt: 104,
  /** Image letterheads: at least this many pixels across (≈ 120 dpi on Letter), so print is not blurry. */
  minImageWidthPx: 1000,
  /** "Recortar orilla": millimetres cropped from every edge of the letterhead (migration 0033). */
  maxEdgeInsetMm: 5,
} as const;

export const LETTERHEAD_PAGE = { width: 612, height: 792 } as const;

export const LetterheadContentType = z.enum(["application/pdf", "image/png", "image/jpeg"]);
export type LetterheadContentType = z.infer<typeof LetterheadContentType>;

const Base64 = z.string().regex(/^[A-Za-z0-9+/]+={0,2}$/, "Archivo inválido.");
const EdgeInsetMm = z
  .number()
  .min(0)
  .max(LETTERHEAD_LIMITS.maxEdgeInsetMm)
  .refine((v) => Math.abs(v * 10 - Math.round(v * 10)) < 1e-9, "Recortar orilla va en décimas de milímetro.");
const MarginPt = z.number().int().min(LETTERHEAD_LIMITS.minMarginPt).max(LETTERHEAD_LIMITS.maxMarginPt);

/**
 * `letterhead.set` (Administrador): upload or replace the letterhead (`file`), and/or change the
 * free zone. Without `file` the current letterhead is kept and only the zone changes.
 */
export const LetterheadSetPayload = z
  .object({
    file: z
      .object({
        content_type: LetterheadContentType,
        base64: Base64.min(8).max(Math.ceil(LETTERHEAD_LIMITS.maxBytes / 3) * 4),
      })
      .optional(),
    content_top_pt: MarginPt,
    content_bottom_pt: MarginPt,
    /** "Recortar orilla" in tenths of a millimetre, 0-5 mm. Omitted: the current value is kept (0 for a new file). */
    edge_inset_mm: EdgeInsetMm.optional(),
  })
  .strict()
  .refine((p) => 792 - p.content_top_pt - p.content_bottom_pt >= LETTERHEAD_LIMITS.minBodyPt, {
    message: "La zona libre es demasiado chica: deja al menos 13 cm para los resultados.",
  });
export type LetterheadSetPayload = z.infer<typeof LetterheadSetPayload>;

/**
 * `letterhead.remove` (Administrador): reports go back to the built-in header. Names the version
 * being removed, so a newer one uploaded meanwhile is never removed by a stale screen. With no
 * letterhead it does nothing.
 */
export const LetterheadRemovePayload = z.object({ letterhead_id: Uuid }).strict();
export type LetterheadRemovePayload = z.infer<typeof LetterheadRemovePayload>;

export const LetterheadInfo = z.object({
  id: Uuid,
  content_type: LetterheadContentType,
  byte_size: z.number().int(),
  sha256_hex: z.string().length(64),
  /** Page size in points for a PDF; pixel size for an image. */
  width: z.number(),
  height: z.number(),
  content_top_pt: z.number().int(),
  content_bottom_pt: z.number().int(),
  /** Millimetres cropped from every edge before the letterhead is fitted to the page. */
  edge_inset_mm: z.number(),
  updated_at: IsoDateTime,
});
export type LetterheadInfo = z.infer<typeof LetterheadInfo>;

/** GET /v1/lab/letterhead — null when the laboratory has none (reports use the built-in header). */
export const LabLetterhead = z.object({ letterhead: LetterheadInfo.nullable() });
export type LabLetterhead = z.infer<typeof LabLetterhead>;

export const LetterheadSetResult = LabLetterhead;
export type LetterheadSetResult = LabLetterhead;

/**
 * GET /v1/lab/letterhead/sample.pdf — a synthetic report ("EJEMPLO") over the current letterhead,
 * with a free zone that may differ from the stored one, for the live preview in Configuración.
 */
export const LetterheadSampleQuery = z.object({
  top: z.coerce.number().int().min(LETTERHEAD_LIMITS.minMarginPt).max(LETTERHEAD_LIMITS.maxMarginPt).optional(),
  bottom: z.coerce.number().int().min(LETTERHEAD_LIMITS.minMarginPt).max(LETTERHEAD_LIMITS.maxMarginPt).optional(),
  /** "Recortar orilla" in mm, to preview before saving (the stored one by default). */
  inset: z.coerce.number().min(0).max(LETTERHEAD_LIMITS.maxEdgeInsetMm).optional(),
  /** false = the same layout without the background (pre-printed paper). */
  letterhead: QueryBool.default(true),
});
export type LetterheadSampleQuery = z.infer<typeof LetterheadSampleQuery>;
